import React from 'react';
import { CircleArrowOutUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FileType } from '../../../types';
import defaultThumbnail from '../../../assets/recording/DefaultThumbnail.svg';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../lib/utils';
import { getFileUrl } from '../../../lib/routingUtils';

interface CitationTooltipProps {
  fileId: string;
  timestamp: string;
  fileName?: string;
  file?: FileType;
  isActive?: boolean;
  thumbnailUrl?: string | null;
  isLoading?: boolean;
  onClick: () => void;
  formatTimestamp: (seconds: string) => string;
  children?: React.ReactNode;
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  fileId,
  timestamp,
  fileName,
  file,
  isActive = false,
  thumbnailUrl,
  isLoading = false,
  onClick,
  formatTimestamp,
  children,
}) => {
  const { t } = useTranslation();
  const displayFileName = fileName || file?.name || 'File';

  // Generate proper URL for the link
  const getHref = () => {
    if (file?.teamId) {
      const fileUrl = getFileUrl({
        teamId: file.teamId,
        parentId: file.parentId || 'recents',
        fileId,
        withBasePath: true,
      });
      return `${fileUrl}?t=${timestamp}`;
    }
    const basePath = window.location.pathname;
    return `${basePath}#/file/${fileId}?t=${timestamp}`;
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={getHref()}
            onClick={(e) => {
              e.preventDefault();
              onClick();
            }}
            className={cn(
              'inline-flex items-center justify-center',
              'w-auto h-auto p-0.5 rounded-full',
              'bg-transparent',
              'relative align-middle mx-1',
              'no-underline hover:no-underline',
              isActive
                ? 'text-primary opacity-100'
                : 'text-muted-foreground opacity-70 hover:opacity-100',
              'hover:text-primary',
            )}
          >
            {children === '→' ? (
              <CircleArrowOutUpRight className="w-[14px] h-[14px]" />
            ) : (
              children || (
                <CircleArrowOutUpRight className="w-[14px] h-[14px]" />
              )
            )}
          </a>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] p-2 bg-background border rounded-2xl shadow-lg"
        >
          {thumbnailUrl && !isLoading && (
            <div className="w-full h-[158px] mb-2 rounded-xl overflow-hidden bg-muted flex justify-center items-center">
              <img
                src={thumbnailUrl}
                alt={t('assistant.citation.thumbnail', {
                  timestamp: formatTimestamp(timestamp),
                })}
                className={cn(
                  'w-full h-full object-contain',
                  thumbnailUrl === defaultThumbnail && 'dark:invert',
                )}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const target = e.currentTarget;
                  if (target.src !== defaultThumbnail) {
                    target.src = defaultThumbnail;
                  }
                }}
              />
            </div>
          )}

          {isLoading && (
            <Skeleton className="w-full h-[158px] mb-2 rounded-xl" />
          )}

          <p className="text-sm text-foreground">
            {t('assistant.citation.goToSource', {
              fileName: displayFileName,
              timestamp: formatTimestamp(timestamp),
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CitationTooltip;
